import {Box, Typography} from "@material-ui/core";
import React, {useEffect, useMemo} from "react";
import NoMessageArrow from "view/images/NoMessageArrow";
import AddIcon from "@material-ui/icons/Add";
import {grey} from "@material-ui/core/colors";
import {DocumentData} from "api/firebase";
import messagingService from "api/messaging";
import FlexBox from "view/components/Layout/FlexBox";
import useExhibitorPairsByStandIds from "hooks/exhibitorPair/useExhibitorPairsByStandIds";
import groupPairsByStand, {findStandLogo} from "tools/chat/groupPairsByStand";
import {useStyles} from "./style";
import ConversationItem from "./components/ConversationItem";

type NavBarProps = {
  showId: string;
  userId: string;
  userRole: string;
  isLoading: boolean;
  selectedConversationId: string;
  selectConversation: (id: string) => void;
  createChat: () => void;
  conversations?: DocumentData;
};

const sortConversations = (
  conversations: {
    key: string;
    data: any;
  }[]
) => {
  return conversations.sort(
    (a, b) => b?.data?.date?.seconds * 1000 - a?.data?.date?.seconds * 1000
  );
};
const NavBar = ({
  showId,
  userId,
  userRole,
  conversations,
  isLoading,
  selectedConversationId,
  createChat,
  selectConversation,
}: NavBarProps) => {
  const classes = useStyles();

  const id = userRole === "organiser" ? "organisers" : userId;
  const userConversations = conversations?.[id];
  const conversationStandIds = useMemo(
    () => Object.keys(userConversations ?? {}),
    [userConversations]
  );

  const [pairs, isLoadingPair] = useExhibitorPairsByStandIds({
    showId,
    legacyStandIds: conversationStandIds,
  });

  const standsWithCompanies = useMemo(() => groupPairsByStand(pairs), [pairs]);

  useEffect(() => {
    if (standsWithCompanies.length) {
      messagingService.updateLastSeenOrganiser(
        showId,
        standsWithCompanies,
        userId
      );
    }
  }, [standsWithCompanies, userId, showId]);

  const Conversations = () => {
    const notReady = !userConversations || isLoading || isLoadingPair;
    if (notReady) {
      return (
        <FlexBox
          alignCenter
          justifyCenter
          height={250}
          width="100%"
          fontSize="16px"
          color={grey[800]}
        >
          Start your chat here
        </FlexBox>
      );
    }

    const sortedConversations = sortConversations(
      conversationStandIds.map((key) => ({
        key,
        data: userConversations[key],
      }))
    );

    if (!sortedConversations.length) return null;

    return (
      <>
        {sortedConversations.map(({key, data}) => {
          let imgUrl = data?.userInfo?.logo || "";
          let standName = data?.userInfo?.name;
          const item = standsWithCompanies.find(
            (currentStand) => currentStand.stand.standId === key
          );

          if (item) {
            const companyLogo = findStandLogo(item.pairs);
            if (companyLogo) {
              imgUrl = companyLogo;
            }

            if (item.allCompaniesNames && item.stand.name) {
              if (item.stand.suffixName) {
                standName = `(${item.stand.name} (${item.stand.suffixName})) ${item.allCompaniesNames}`;
              } else {
                standName = `(${item.stand.name}) ${item.allCompaniesNames}`;
              }
            }
          }
          return (
            <ConversationItem
              key={key}
              selectConversation={selectConversation}
              standId={key}
              standName={standName}
              subTitle="exhibitor"
              lastMessage={data.lastMessage}
              unread={data.unreadCount}
              timestamp={data.date}
              imgUrl={imgUrl}
              selectedConversationId={selectedConversationId}
            />
          );
        })}
      </>
    );
  };

  return (
    <Box>
      <Box className={classes.container}>
        <Typography variant="h2" component="div">
          Chat List
        </Typography>
        <Box className={classes.newChat} onClick={createChat}>
          <AddIcon />
        </Box>
        {!userConversations && (
          <Box className={classes.chatArrow}>
            <NoMessageArrow />
          </Box>
        )}
      </Box>
      <Box
        style={{
          overflow: "auto",
          maxHeight: "calc(100vh - 350px)",
        }}
      >
        <Conversations />
      </Box>
    </Box>
  );
};

export default NavBar;
